"use client"

import { useEffect } from "react"
import { useState } from "react"
import type React from "react"
import { useRef, memo, useCallback, useMemo } from "react"
import Image from "next/image"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { useTranslations } from "next-intl"
import { Button } from "@/components/ui/button"
import dynamic from "next/dynamic"
import { Building2, Bookmark, ChevronLeft, ChevronRight, ChevronsRight, MapPin, CheckCircle2, Rows, LayoutGrid, ArrowRight, Search, ChevronDown, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { createClient } from "@/lib/supabase/client"
import { useToast } from "@/hooks/use-toast"
import { canonicalCityKey, formatCity, formatState } from "@/lib/format-location"
import { buildSearchHaystack, tokenizeSearchQuery, matchesSearchTokens } from "@/lib/listing-search"
import { Input } from "@/components/ui/input"
import { ListingCardSkeleton } from "@/components/loading-skeleton"
import { ListingsSortModal, SortTrigger, type SortMode } from "@/components/listings-sort-modal"
import { shopTypes } from "@/data/filters"
import { SignUpPromptModal } from "@/components/signup-prompt-modal"
import { useUserRole } from "@/providers/user-role-provider"
import { readClientView } from "@/lib/client-view"
import { PageLoader } from "@/components/page-loader"
import { PageFrame } from "@/components/page-frame"
import { COUNTRIES, COUNTRY_ROSTER, countryCodeForState } from "@/lib/country-from-state"
import { CountryFlag } from "@/components/country-flag"
import { ListingsFilterModal } from "@/components/listings-filter-modal"

// Remembered answer to the International switch. An explicit choice outranks
// whatever Vercel's geo says on the next visit — someone in Toronto who wants
// the US listings should get them without re-toggling every time.
const INTL_MODE_KEY = "booth-listings-intl-mode"

// Country the edge resolved for this visitor, written by proxy.ts. Absent on
// localhost (no edge geo in `next dev`) and for anyone whose IP Vercel cannot
// place; both mean "treat as US", which is the default anyway.
function readCountryCookie(): string | null {
  if (typeof document === "undefined") return null
  const match = document.cookie.match(/(?:^|;\s*)booth-country=([^;]*)/)
  const code = match?.[1]?.toUpperCase()
  return code && /^[A-Z]{2}$/.test(code) ? code : null
}

type Listing = {
  id: string
  name: string
  city: string
  state: string
  cover_image: string
  images: string[]
  monthlyPrice: number
  availableBooths: number
  totalBooths: number
  shopType: string
  lat: number
  lng: number
  slug: string
  booth_plans?: { id: string; plan_type: string; price: number; type: string; description?: string }[]
  boothPlans?: { id: string; plan_type: string; price: number; type: string; description?: string }[]
  hide_rates?: boolean
  services_starting_at?: number | null
  // Present on every /api/shops row and searchable, even though the cards
  // don't render all of them.
  description?: string | null
  address?: string | null
  zip?: string | null
  features?: string[]
  displayPrice?: number
  displayPlanType?: string
  // Sort metadata — set on the client after the initial fetch.
  created_at?: string
  updated_at?: string
  viewCount?: number
  // Time-decayed view score (recent views weighted higher). Powers
  // the "Most popular" sort instead of raw lifetime view count, so
  // long-listed shops can't permanently camp the top of the list.
  popularScore?: number
  /** Manual thumb on the scale for the popular sort. See scripts/1016. */
  sort_boost?: number | null
}

// Shop-type values are stored lowercase ("barbershop"); translate the known
// ones and fall back to the stored value for anything unrecognized.
const KNOWN_SHOP_TYPES = new Set(shopTypes.map((s) => s.value))
function typeLabelFor(value: string | undefined, t: ReturnType<typeof useTranslations>): string {
  const v = (value || "barbershop").toLowerCase()
  return KNOWN_SHOP_TYPES.has(v) ? t(`filters.types.${v}`) : value || t("card.badgeFallback")
}

// Rendered as a real component by next/dynamic, so the translations hook works.
function MapLoadingFallback() {
  const t = useTranslations("pages.listings")
  return (
    <div className="h-full w-full bg-muted animate-pulse flex items-center justify-center">
      <p className="text-sm text-muted-foreground">{t("map.loading")}</p>
    </div>
  )
}

const ListingsMapDynamic = dynamic(() => import("@/components/listings-map").then((mod) => mod.ListingsMap), {
  ssr: false,
  loading: MapLoadingFallback,
})

// Map sizing. Shared by the scroll-driven collapse and the pull-to-resize
// handle, which have to agree or releasing the handle fights the next scroll.
//
// Map height floor — below this the map collapses into a useless stripe and
// you can't pan or read pins. 22vh is just tall enough to keep the zoom
// controls + shop count badge legible.
const MIN_MAP_VH = 22
const MAX_MAP_VH = 85
/** Scroll distance over which the map eases down to its collapsed height. */
const SCROLL_RANGE = 320

const ListingCard = memo(function ListingCard({
  listing,
  savedListings,
  savingId,
  onToggleSave,
  onLearnMore,
  isOwner,
  clientLens,
}: {
  listing: Listing
  savedListings: Record<string, string>
  savingId: string | null
  isOwner?: boolean
  clientLens?: boolean
  onToggleSave: (e: React.MouseEvent, id: string) => void
  onLearnMore: (slug: string) => void
}) {
  const t = useTranslations("pages.listings")
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isHovered, setIsHovered] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  const allImages = []
  if (listing.cover_image) allImages.push(listing.cover_image)
  if (listing.images?.length) allImages.push(...listing.images)
  if (allImages.length === 0) allImages.push("/placeholder.svg?height=400&width=600")

  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return
    const container = scrollRef.current
    const scrollLeft = container.scrollLeft
    const width = container.offsetWidth
    const newIndex = Math.round(scrollLeft / width)
    if (newIndex !== currentIndex && newIndex >= 0 && newIndex < allImages.length) {
      setCurrentIndex(newIndex)
    }
  }, [currentIndex, allImages.length])

  const scrollToImage = (index: number) => {
    if (!scrollRef.current) return
    scrollRef.current.scrollTo({
      left: index * scrollRef.current.offsetWidth,
      behavior: "smooth",
    })
    setCurrentIndex(index)
  }

  const handlePrev = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    scrollToImage(currentIndex === 0 ? allImages.length - 1 : currentIndex - 1)
  }

  const handleNext = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    scrollToImage(currentIndex === allImages.length - 1 ? 0 : currentIndex + 1)
  }

  const getPricingDisplay = () => {
    if (listing.hide_rates) {
      return {
        price: 0,
        period: "",
        description: null,
        isCommission: false,
        isHidden: true,
      }
    }

    const allPlans = listing.booth_plans || listing.boothPlans || []

    // Helper to get plan type - supports both old and new column names
    const getPlanType = (plan: any) => plan.billing_period || plan.plan_type || plan.type || "monthly"

    // Filter out disabled plans and plans with no real price
    const plans = allPlans.filter((p: any) => Number(p.price) > 0)

    if (plans.length === 0) {
      return {
        price: Number(listing.monthlyPrice) || 0,
        period: "month",
        description: null,
        isCommission: false,
        isHidden: false,
      }
    }

    // Sort by monthly equivalent to find cheapest option
    const sortedPlans = [...plans].sort((a, b) => {
      const getMonthlyEquivalent = (plan: any) => {
        const price = Number(plan.price) || 0
        const type = getPlanType(plan)
        if (type === "daily") return price * 30
        if (type === "weekly") return price * 4
        return price // monthly or commission
      }
      return getMonthlyEquivalent(a) - getMonthlyEquivalent(b)
    })

    const cheapestPlan = sortedPlans[0]

    if (!cheapestPlan) {
      return {
        price: Number(listing.monthlyPrice) || 0,
        period: "month",
        description: null,
        isCommission: false,
        isHidden: false,
      }
    }

    const planPrice = Number(cheapestPlan.price) || 0
    const planType = getPlanType(cheapestPlan)
    const periodLabel =
      planType === "daily"
        ? "day"
        : planType === "weekly"
          ? "week"
          : planType === "commission"
            ? ""
            : "month"

    return {
      price: planPrice,
      period: periodLabel,
      description: cheapestPlan.description || null,
      isCommission: planType === "commission",
      isHidden: false,
    }
  }

  const pricing = getPricingDisplay()

  return (
    <Link
      href={`/listings/${listing.slug || listing.id}`}
      className="relative group block"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative rounded-3xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 aspect-[4/3]">
        {/* Full image with carousel */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex w-full h-full overflow-x-auto snap-x snap-mandatory scrollbar-hide"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {/* Index leads the key. Shops do upload the same file twice, and
              keying on the URL alone then produced duplicate React keys —
              that is the console error, and why a repeated photo could be
              dropped from the carousel entirely. */}
          {allImages.map((img, idx) => (
            <div key={`${idx}-${img ?? ""}`} className="relative flex-none w-full h-full snap-center snap-always">
              <Image
                src={img || "/placeholder.svg"}
                alt={`${listing.name} ${idx + 1}`}
                fill
                className="object-cover"
                priority={idx === 0}
                loading={idx === 0 ? "eager" : "lazy"}
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 33vw, 25vw"
              />
            </div>
          ))}
        </div>

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent pointer-events-none" />

        {/* Navigation Arrows */}
        {allImages.length > 1 && (
          <>
            <button
              onClick={handlePrev}
              className={`hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-card/80 items-center justify-center z-30 transition-opacity duration-200 ${
                isHovered ? "opacity-100" : "opacity-0"
              } hover:bg-accent`}
            >
              <ChevronLeft className="w-4 h-4 text-foreground" />
            </button>
            <button
              onClick={handleNext}
              className={`hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-card/80 items-center justify-center z-30 transition-opacity duration-200 ${
                isHovered ? "opacity-100" : "opacity-0"
              } hover:bg-accent`}
            >
              <ChevronRight className="w-4 h-4 text-foreground" />
            </button>
          </>
        )}

        {/* Dots — inline with city line */}
        {allImages.length > 1 && (
          <div className="absolute bottom-3.5 left-0 right-0 flex justify-center gap-1 z-20 pointer-events-none">
            {allImages.map((img, idx) => (
              <button
                key={`indicator-${idx}`}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  scrollToImage(idx)
                }}
                className={`pointer-events-auto h-1 rounded-full transition-all duration-200 ${
                  idx === currentIndex ? "bg-card w-4" : "bg-card/50 w-1 hover:bg-accent/80"
                }`}
              />
            ))}
          </div>
        )}

        {/* Shop Type Badge - Top Left */}
        <div className="absolute top-2.5 left-2.5 z-20 flex items-center">
          <span className="px-2 py-1 rounded-full bg-white text-[11px] font-medium text-black capitalize leading-none">{typeLabelFor(listing.shopType, t)}</span>
        </div>

        {/* Save - Top Right (barbers only) */}
        {!isOwner && (
          <button
            onClick={(e) => onToggleSave(e, listing.id)}
            disabled={savingId === listing.id}
            aria-label={savedListings[listing.id] ? t("card.removeAria") : t("card.saveAria")}
            className="absolute top-2.5 right-2.5 z-20 transition-transform duration-200 hover:scale-110 disabled:opacity-50"
          >
            <Bookmark
              strokeWidth={1.75}
              className={`w-5 h-5 transition-colors drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)] ${
                savedListings[listing.id] ? "fill-white text-white" : "text-white"
              }`}
            />
          </button>
        )}

        {/* Text overlay on bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-3 z-10">
          <div className="flex items-end justify-between gap-2">
            <div className="min-w-0">
              <h3 className="font-semibold text-sm text-white leading-tight line-clamp-1">{listing.name}</h3>
              <p className="text-[11px] text-white/70 mt-0.5">
                {formatCity(listing.city)}, {formatState(listing.state)}
              </p>
            </div>
            {clientLens ? (
              Number(listing.services_starting_at) > 0 ? (
                <span className="text-sm font-bold text-white whitespace-nowrap flex-shrink-0">
                  {t("card.servicesFrom", { price: `$${Number(listing.services_starting_at).toLocaleString()}` })}
                </span>
              ) : null
            ) : pricing.isHidden || pricing.price <= 0 ? (
              <span className="text-sm font-medium text-white/90 whitespace-nowrap flex-shrink-0">
                {t("card.inquire")}
              </span>
            ) : (
              <span className="text-sm font-bold text-white whitespace-nowrap flex-shrink-0">
                {pricing.isCommission
                  ? `${pricing.price}%`
                  : `$${pricing.price.toLocaleString()}/${
                      pricing.period === "month"
                        ? t("card.perMonth")
                        : pricing.period === "week"
                          ? t("card.perWeek")
                          : t("card.perDay")
                    }`}
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  )
})

// The founder's studio. Real listing, real views — it just never leads the
// popularity sort (see the "popular" case below).
const HOUSE_SHOP_ID = "de831f57-a2c4-4698-a341-b7db169d9db5"
// Shops that never open the grid, under any sort. Neighborhood Cutz (Merced)
// is listed with an AI-generated cover photo; the owner has been asked for a
// real one, and until it lands the listing stays but never leads the page.
const NEVER_FIRST_SHOP_IDS: ReadonlySet<string> = new Set([
  "5c310ba7-9ab3-495a-b504-4d8cd0e08fca", // Neighborhood Cutz, Merced
])

export default function ListingsPage() {
  const t = useTranslations("pages.listings")
  const { role, userId, isClient } = useUserRole()
  // Client lens — true clients and pros in Client mode (cookie) are shopping
  // for haircuts, not chairs. Booth rent stays off their cards and map pins;
  // the owner-set "services starting at" price shows instead when present.
  // Same resolution the listing detail page uses (isClient || readClientView).
  const [clientModeActive, setClientModeActive] = useState(false)
  useEffect(() => { setClientModeActive(readClientView()) }, [])
  const clientLens = isClient || clientModeActive
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedCity, setSelectedCity] = useState<string>("all")
  // Country filter in front of the city pills. Forty city pills scrolled off
  // the right edge and nobody scrolls a filter bar; eight flags fit at a
  // glance. No separate city text box — the main search bar below already
  // indexes city and state (see buildSearchHaystack).
  const [selectedCountry, setSelectedCountry] = useState<string>("all")
  // International mode — mobile only. Off, the second row of the mobile bar
  // lists US cities; on, it lists the country roster. BOOTH's shops are
  // overwhelmingly US, so a row of flags was spending a 390px row asking a
  // question most visitors had already answered: the city they want is the
  // one thing they always need, and it was the thing behind a tap.
  //
  // Desktop is untouched — it has the width to show flags AND cities at once,
  // so it never needs a mode.
  //
  // Default: off, i.e. the US. A visitor Vercel places outside the US starts
  // with it on and their own country selected (proxy.ts writes booth-country);
  // an explicit choice is remembered and outranks geo.
  const [intlMode, setIntlMode] = useState(false)
  // Applied in an effect, not a useState initializer: this component renders
  // on the server too, and reading document/localStorage during the initial
  // render is a hydration mismatch. The listings layout holds a PageLoader
  // until auth resolves, so nothing is painted before this lands. Same shape
  // as the clientModeActive effect above.
  //
  // Reads the width directly rather than leaning on the `isMobile` state
  // declared further down: that one is seeded false and corrected in its own
  // effect, so at this point it still claims desktop on a phone. Same 768
  // breakpoint, which is also the `md:` the two filter rows switch on.
  useEffect(() => {
    if (typeof window === "undefined" || window.innerWidth >= 768) return

    const saved = window.localStorage.getItem(INTL_MODE_KEY)
    if (saved === "1" || saved === "0") {
      setIntlMode(saved === "1")
      setSelectedCountry(saved === "1" ? "all" : "US")
      return
    }
    const geo = readCountryCookie()
    if (geo && geo !== "US") {
      setIntlMode(true)
      setSelectedCountry(geo)
    } else {
      setSelectedCountry("US")
    }
  }, [])

  // Toggling the mode is not the same as filtering inside it: leaving
  // international pins the country back to the US, and entering it opens the
  // roster wide rather than stranding the reader on whatever single country
  // they had. Either way the city choice is dropped, because a city from the
  // country you just left is not a filter, it is an empty grid.
  const changeIntlMode = useCallback((next: boolean) => {
    setIntlMode(next)
    setSelectedCountry(next ? "all" : "US")
    setSelectedCity("all")
    try {
      window.localStorage.setItem(INTL_MODE_KEY, next ? "1" : "0")
    } catch {
      // Private mode / storage disabled. The mode still works for this visit.
    }
  }, [])
  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const [selectedType, setSelectedType] = useState<string>("all")
  // City-filter horizontal scroll: a small left-side caret nudges the row
  // right so users know the chips scroll left→right (refs for both the
  // desktop and mobile city rows).
  const countriesScrollRef = useRef<HTMLDivElement>(null)
  const citiesScrollRefMobile = useRef<HTMLDivElement>(null)
  const nudgeCities = useCallback((ref: React.RefObject<HTMLDivElement | null>) => {
    ref.current?.scrollBy({ left: 180, behavior: "smooth" })
  }, [])
  const [savedListings, setSavedListings] = useState<Record<string, string>>({})
  const [savingId, setSavingId] = useState<string | null>(null)
  const [currentUser, setCurrentUser] = useState<any>(null)
  const [listings, setListings] = useState<Listing[]>([])
  const [loading, setLoading] = useState(true)
  const [waitlistOpen, setWaitlistOpen] = useState(false)
  const [mobileMapExpanded, setMobileMapExpanded] = useState(false)
  // Fullscreen map carries data-floating-nav="hide" — nudge the launcher
  // obscured-zone hook to re-measure when the overlay mounts/unmounts.
  useEffect(() => {
    window.dispatchEvent(new Event("resize"))
  }, [mobileMapExpanded])
  // Single density toggle that controls grid columns at every
  // breakpoint. dense = 2 cols mobile / 5 cols desktop, sparse = 1
  // col mobile / 3 cols desktop. Replaces the old mobile-only toggle.
  const [viewDensity, setViewDensity] = useState<"dense" | "sparse">("sparse")
  // Sort mode for the listings — default 'popular' (most-viewed first).
  // Persisted to URL so visitors can share sorted views.
  const [sortMode, setSortMode] = useState<SortMode>("random")
  // Where ABSworkspace lands under Most popular — picked once per page load
  // so it doesn't hop around as filters change.
  const [houseSeed] = useState(() => Math.random())
  const [sortOpen, setSortOpen] = useState(false)
  const [selectedMapListing, setSelectedMapListing] = useState<string | null>(null)

  // The shop lifted out of the map and parked at the head of the list.
  //
  // Tapping View on a map card used to scroll the page down to wherever that
  // shop happened to sit — row 2 or row 40 — and flash an outline on it. The
  // viewer was thrown into the middle of a list with no sense of where they
  // had landed, and on a long list the scroll itself was the whole
  // experience. Now the shop comes to them: it becomes the first card under
  // the Listings bar and the page moves only as far as that bar.
  const [pinnedFromMap, setPinnedFromMap] = useState<string | null>(null)
  const listingsTopRef = useRef<HTMLDivElement | null>(null)
  const [showSignUpModal, setShowSignUpModal] = useState(false)
  // svh percentage, updated on mount for mobile. 32 -> 40: at a third of the
  // viewport the map was a strip, and the whole point of leading with it is
  // that someone can see where the shops actually are. Still draggable.
  const [mapHeight, setMapHeight] = useState(40)
  const [isDraggingMap, setIsDraggingMap] = useState(false)
  const dragStateRef = useRef<{ startY: number; startVh: number }>({ startY: 0, startVh: 0 })
  const scrollRafRef = useRef<number>(0)
  const userMaxVhRef = useRef<number>(32)
  const [isMobile, setIsMobile] = useState(false)
  // True only where the mode exists and is off: the mobile bar, showing US
  // cities. Desktop is never in "US mode" — it shows flags and cities side by
  // side and keeps its all-countries default, so anything that pins the
  // country to the US has to ask this and not just `!intlMode`.
  const usMode = isMobile && !intlMode
  const [listingsExpanded, setListingsExpanded] = useState(false)
  const listingsContainerRef = useRef<HTMLDivElement>(null)

  const router = useRouter()
  const { toast } = useToast()
  const supabase = createClient()

  // Sync sort mode to URL (?sort=) so a sorted view is shareable.
  // Reads on mount and writes whenever the user changes the sort.
  useEffect(() => {
    if (typeof window === "undefined") return
    const params = new URLSearchParams(window.location.search)
    const raw = params.get("sort")
    if (raw && ["random", "popular", "price_asc", "price_desc"].includes(raw)) {
      setSortMode(raw as SortMode)
    }
  }, [])
  useEffect(() => {
    if (typeof window === "undefined") return
    const params = new URLSearchParams(window.location.search)
    if (sortMode === "random") params.delete("sort")
    else params.set("sort", sortMode)
    const qs = params.toString()
    const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname
    window.history.replaceState(null, "", url)
  }, [sortMode])

  // Fetch current user and their saved listings on mount
  useEffect(() => {
    async function fetchUserAndSaved() {
      const { data: { user } } = await supabase.auth.getUser()
      setCurrentUser(user)

      if (user) {
        // Fetch user's saved listings
        const { data: saved } = await supabase
          .from("saved_shops")
          .select("shop_id")
          .eq("barber_id", user.id)

        if (saved) {
          const savedMap: Record<string, string> = {}
          saved.forEach((item: { shop_id: string }) => {
            savedMap[item.shop_id] = item.shop_id
          })
          setSavedListings(savedMap)
        }
      }
    }
    fetchUserAndSaved()
  }, [])

  const handleToggleSave = useCallback(async (e: React.MouseEvent, id: string) => {
    e.preventDefault()
    e.stopPropagation()

    // Check if user is authenticated
    if (!currentUser) {
      setShowSignUpModal(true)
      return
    }

    setSavingId(id)

    try {
      // Use saved_shops table (consistent with barber dashboard)
      const { data, error } = await supabase
        .from("saved_shops")
        .select("*")
        .eq("barber_id", currentUser.id)
        .eq("shop_id", id)
        .maybeSingle()

      if (error) {
        console.error("Error fetching saved status:", error)
        toast({
          title: t("toasts.errorTitle"),
          description: t("toasts.fetchSavedFailed"),
          variant: "destructive",
        })
        setSavingId(null)
        return
      }

      if (data) {
        // Already saved - remove it
        await supabase.from("saved_shops").delete().eq("barber_id", currentUser.id).eq("shop_id", id)
        setSavedListings((prev) => {
          const updated = { ...prev }
          delete updated[id]
          return updated
        })
        toast({
          title: t("toasts.removedTitle"),
          description: t("toasts.removedDesc"),
        })
      } else {
        // Not saved - add it
        await supabase.from("saved_shops").insert({ barber_id: currentUser.id, shop_id: id })
        setSavedListings((prev) => ({ ...prev, [id]: id }))
        toast({
          title: t("toasts.savedTitle"),
          description: t("toasts.savedDesc"),
        })
      }
    } catch (err) {
      console.error("Error toggling save:", err)
      toast({
        title: t("toasts.errorTitle"),
        description: t("toasts.updateFailed"),
        variant: "destructive",
      })
    } finally {
      setSavingId(null)
    }
  }, [currentUser, supabase, toast])

  const handleLearnMore = useCallback(
    (slug: string) => {
      router.push(`/listings/${slug}`)
    },
    [router],
  )

  // Tapping a pin SELECTS it: the map opens its preview card and the matching
  // row below is marked, but the viewer stays on the map. It used to scroll
  // the page (and collapse a fullscreen map) on the pin tap itself, which
  // skipped the card entirely — the viewer was moved before they had seen
  // what they'd tapped, and on a fullscreen map the map closed under them.
  const handleMapListingSelect = useCallback((listingId: string) => {
    setSelectedMapListing(listingId)
  }, [])

  // The card's View is what moves them — the second, deliberate step.
  //
  // It hoists rather than hunts. The shop becomes the first card under the
  // Listings bar, and the page scrolls to that bar — a fixed, recognisable
  // place — instead of to the shop's old position somewhere down the list.
  // The viewer always lands looking at the same heading with their shop
  // directly beneath it, whether it was row 2 or row 40.
  //
  // The old version wrote outline/borderRadius/transition onto the DOM node
  // by hand and unset them on a 3s timer. That is gone: the mark is state
  // now, so React owns it and it cannot be left painted on a node that has
  // since been recycled into a different shop by a filter change.
  const handleMapListingView = useCallback((listingId: string) => {
    setSelectedMapListing(listingId)
    setPinnedFromMap(listingId)

    // With the map expanded to fullscreen the list is behind it — collapse it
    // first, or the scroll happens somewhere nobody can see.
    if (mobileMapExpanded) setMobileMapExpanded(false)

    // One frame past the collapse so the bar is where it will finally sit.
    setTimeout(() => {
      listingsTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    }, mobileMapExpanded ? 320 : 150)
  }, [mobileMapExpanded])

  // Derive the city pill list from the actual loaded listings instead of
  // a hardcoded US-only set. Unique non-empty cities, alphabetized; the
  // shop's stored casing wins (so 'tokyo' renders as 'Tokyo' if the seed
  // wrote it that way). 'All Cities' always pinned first.
  // The flag row is the roster, not the inventory. Countries BOOTH has shops
  // in come first, ordered by how many; the rest follow in COUNTRY_ROSTER
  // order so scrolling reads like crossing a map. A country with no shops is
  // shown dimmed and is not clickable — it says where BOOTH is going without
  // dead-ending anyone in an empty result.
  const dynamicCountries = (() => {
    const counts = new Map<string, number>()
    listings.forEach((l) => {
      const code = countryCodeForState(l.state)
      if (code) counts.set(code, (counts.get(code) ?? 0) + 1)
    })
    const withShops = Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([code, count]) => ({ ...COUNTRIES[code], code, count }))
    const rest = COUNTRY_ROSTER.filter((code) => !counts.has(code) && COUNTRIES[code]).map((code) => ({
      ...COUNTRIES[code],
      code,
      count: 0,
    }))
    return [...withShops, ...rest]
  })()

  const dynamicCities = (() => {
    const seen = new Map<string, string>() // canonical key → display label
    listings.forEach((l) => {
      const c = (l.city || "").trim()
      if (!c) return
      if (selectedCountry !== "all" && countryCodeForState(l.state) !== selectedCountry) return
      // Canonical key merges alias spellings ("Mexico City" / "CDMX" /
      // "Ciudad de México") into ONE pill instead of one per spelling.
      const key = canonicalCityKey(c)
      if (!seen.has(key)) {
        seen.set(key, formatCity(key))
      }
    })
    const sorted = Array.from(seen.values()).sort((a, b) => a.localeCompare(b))
    return [
      { value: "all", label: t("filters.allCities") },
      ...sorted.map((c) => ({ value: c, label: c })),
    ]
  })()

  // id → searchable text. Rebuilt only when the listings themselves change,
  // so typing doesn't re-flatten every shop on every keystroke.
  const searchIndex = useMemo(() => {
    const index = new Map<string, string>()
    listings.forEach((listing) =>
      index.set(listing.id, buildSearchHaystack(listing, typeLabelFor(listing.shopType, t))),
    )
    return index
  }, [listings, t])

  const searchTokens = useMemo(() => tokenizeSearchQuery(searchQuery), [searchQuery])

  // Every filter axis in one place, so "Clear all" cannot quietly forget one
  // the way the empty state's button forgot country.
  //
  // Outside international mode the country is pinned to US as the MODE, not
  // as a choice the reader made — counting it would leave the bar reading
  // "1 filter on" for everyone on first load, and clearing it would drop the
  // US row into showing every country's cities.
  const activeFilterCount =
    (selectedType !== "all" ? 1 : 0) +
    (selectedCity !== "all" ? 1 : 0) +
    (!usMode && selectedCountry !== "all" ? 1 : 0)
  const clearAllFilters = () => {
    setSelectedType("all")
    setSelectedCity("all")
    setSelectedCountry(usMode ? "US" : "all")
  }

  // Stable reference — scroll-driven mapHeight re-renders the page every
  // frame; without memoization a fresh array each time re-triggers map init.
  const filteredListings = useMemo(() => {
    const filtered = listings.filter((listing) => {
      if (selectedCountry !== "all" && countryCodeForState(listing.state) !== selectedCountry) return false
      if (selectedCity !== "all" && canonicalCityKey(listing.city) !== canonicalCityKey(selectedCity)) return false
      if (selectedType !== "all" && listing.shopType !== selectedType) return false
      if (searchTokens.length > 0 && !matchesSearchTokens(searchIndex.get(listing.id) || "", searchTokens)) {
        return false
      }
      return true
    })

    // Sort the filtered list according to the selected mode. Cloned
    // before sort so we don't mutate the original `listings` array.
    const arr = [...filtered]
    switch (sortMode) {
      case "random":
        // The fetched array is already shuffled once per page load (see
        // fetchListings); filtering keeps that order, so nothing to do.
        break
      case "popular": {
        // Most visited, plainly: total listing views, tiebreak on the
        // time-decayed score (recent views win a tie), then shop age. The
        // freshness bonus that used to sit on top made this "recently
        // posted" in practice — a shop seeded yesterday outranked one with
        // months of visits. sort_boost still curates (negative demotes,
        // positive promotes, never hides).
        arr.sort((a, b) => {
          const boost = Number(b.sort_boost ?? 0) - Number(a.sort_boost ?? 0)
          if (boost !== 0) return boost
          const views = (b.viewCount ?? 0) - (a.viewCount ?? 0)
          if (views !== 0) return views
          const recent = (b.popularScore ?? 0) - (a.popularScore ?? 0)
          if (recent !== 0) return recent
          return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
        })
        // House rule: ABSworkspace (the founder's studio) never leads Most
        // popular. It stays in the list at a random spot past the top rows.
        const houseIdx = arr.findIndex((l) => l.id === HOUSE_SHOP_ID)
        if (houseIdx !== -1 && arr.length > 4) {
          const [house] = arr.splice(houseIdx, 1)
          const at = 3 + Math.floor(houseSeed * (arr.length - 3))
          arr.splice(at, 0, house)
        }
        break
      }
      case "price_asc":
        arr.sort((a, b) => (a.monthlyPrice || 0) - (b.monthlyPrice || 0))
        break
      case "price_desc":
        arr.sort((a, b) => (b.monthlyPrice || 0) - (a.monthlyPrice || 0))
        break
    }
    // Never-first shops: if one ends up leading — the random shuffle, a
    // popularity tie, a price sort — it drops to a spot past the top rows,
    // same seat the house rule uses. Order among the rest is untouched.
    if (arr.length > 1 && NEVER_FIRST_SHOP_IDS.has(arr[0].id)) {
      const [lead] = arr.splice(0, 1)
      const at = Math.min(arr.length, 3 + Math.floor(houseSeed * Math.max(1, arr.length - 3)))
      arr.splice(at, 0, lead)
    }
    return arr
  }, [listings, selectedCountry, selectedCity, selectedType, searchTokens, searchIndex, sortMode, houseSeed])

  // What the grid actually renders: the filtered order, with a map-pinned
  // shop lifted to the front.
  //
  // Applied AFTER the house and never-first rules on purpose. Those govern
  // the default order — which shop gets the top row when nobody asked for
  // one. This is someone pointing at a specific shop on a map, and an
  // explicit choice outranks a tiebreak. A pinned shop that is no longer in
  // the filtered set (the viewer changed city, or searched) simply is not
  // found, and the list renders untouched.
  const displayListings = useMemo(() => {
    if (!pinnedFromMap) return filteredListings
    const idx = filteredListings.findIndex((l) => l.id === pinnedFromMap)
    if (idx <= 0) return filteredListings
    const arr = [...filteredListings]
    const [pinned] = arr.splice(idx, 1)
    arr.unshift(pinned)
    return arr
  }, [filteredListings, pinnedFromMap])

  const pinnedListing = useMemo(
    () => (pinnedFromMap ? listings.find((l) => l.id === pinnedFromMap) ?? null : null),
    [listings, pinnedFromMap],
  )

  // Any change to what is being browsed retires the pin. Leaving a shop
  // stuck to the top of a list the viewer has since re-filtered would read
  // as a bug rather than as their own earlier tap.
  useEffect(() => {
    setPinnedFromMap(null)
  }, [selectedCountry, selectedCity, selectedType, searchTokens, sortMode])

  useEffect(() => {
    async function fetchListings() {
      try {
        const response = await fetch("/api/shops")
        if (!response.ok) {
          if (response.status === 429 || response.status === 503) {
            console.error("Service temporarily unavailable - please wait and try again")
            toast({
              title: t("toasts.unavailableTitle"),
              description: t("toasts.unavailableDesc"),
              variant: "destructive",
            })
            setLoading(false)
            return
          }
          console.error("API response not OK:", response.status, response.statusText)
          setLoading(false)
          return
        }
        const data = await response.json()

        // Data now includes images and boothPlans from API - no separate Supabase calls needed
        const enrichedListings = data.map((listing: Listing) => ({
          ...listing,
          images: listing.images || [],
          boothPlans: listing.boothPlans || [],
        }))

        // Shuffle once per page load so the default order is random: no shop
        // is always first, and shops seeded the same day can't own the top of
        // the page. Fisher–Yates; the other sort modes re-sort a copy.
        for (let i = enrichedListings.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1))
          ;[enrichedListings[i], enrichedListings[j]] = [enrichedListings[j], enrichedListings[i]]
        }
        setListings(enrichedListings)
        setLoading(false)

        // Fetch view rows in the background and compute both a raw
        // count and a time-decayed score (14-day half-life). The
        // decayed score powers "Most popular" so a shop that's been
        // trending this week beats a shop that got views a year ago.
        // Failure is silent; sorts that depend on it gracefully degrade.
        try {
          const supabase = createClient()
          const { data: views } = await supabase
            .from("shop_listing_views")
            .select("shop_id, created_at")
          if (views) {
            const counts: Record<string, number> = {}
            const scores: Record<string, number> = {}
            const now = Date.now()
            const HALF_LIFE_DAYS = 14
            for (const v of views as { shop_id: string; created_at: string }[]) {
              counts[v.shop_id] = (counts[v.shop_id] || 0) + 1
              const ageDays = v.created_at
                ? (now - new Date(v.created_at).getTime()) / (1000 * 60 * 60 * 24)
                : 0
              const weight = Math.exp(-Math.max(0, ageDays) / HALF_LIFE_DAYS)
              scores[v.shop_id] = (scores[v.shop_id] || 0) + weight
            }
            setListings(prev => prev.map(l => ({
              ...l,
              viewCount: counts[l.id] || 0,
              popularScore: scores[l.id] || 0,
            })))
          }
        } catch {
          // view counts are non-critical — silent fail
        }
      } catch (err) {
        console.error("Error fetching listings:", err)
        toast({
          title: t("toasts.errorTitle"),
          description: t("toasts.loadFailed"),
          variant: "destructive",
        })
      }
    }

    fetchListings()
  }, [])

  // Detect mobile + reset map preference whenever the role/expansion changes.
  // Default opens to 48svh so the first listing card peeks below the
  // map in the initial viewport. Listings-expanded drops it to 14.
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
      const preferred = listingsExpanded ? 14 : 48
      userMaxVhRef.current = preferred
      setMapHeight(preferred)
    }
    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => window.removeEventListener("resize", checkMobile)
  }, [listingsExpanded])

  // Scroll-driven map collapse — desktop only. On mobile the
  // continuous height update caused the map library to re-paint
  // every frame and read as glitchy; mobile users collapse the
  // map manually via the "Expand listings" button instead.
  useEffect(() => {
    if (isMobile) return

    const handleScroll = () => {
      if (isDraggingMap) return
      cancelAnimationFrame(scrollRafRef.current)
      scrollRafRef.current = requestAnimationFrame(() => {
        const scrollTop = window.scrollY
        const progress = Math.min(Math.max(scrollTop / SCROLL_RANGE, 0), 1)
        // ease-out-quart: starts fast, lands gently — feels premium
        const eased = 1 - Math.pow(1 - progress, 4)
        const max = userMaxVhRef.current
        // Never let the map shrink below the global minimum — past
        // that it becomes a useless stripe with no usable surface.
        setMapHeight(Math.max(MIN_MAP_VH, max * (1 - eased)))
      })
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", handleScroll)
      cancelAnimationFrame(scrollRafRef.current)
    }
  }, [isMobile, isDraggingMap])

  // Pull-to-resize: pointer events give us unified mouse/touch + Pointer Capture
  // so the gesture follows the user even if they drag outside the handle.
  const handleMapDragStart = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    cancelAnimationFrame(scrollRafRef.current) // kill any pending scroll-driven update
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    dragStateRef.current = { startY: e.clientY, startVh: mapHeight }
    setIsDraggingMap(true)
  }, [mapHeight])

  const handleMapDragMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingMap) return
    const deltaPx = e.clientY - dragStateRef.current.startY
    const deltaVh = (deltaPx / window.innerHeight) * 100
    const next = Math.max(MIN_MAP_VH, Math.min(MAX_MAP_VH, dragStateRef.current.startVh + deltaVh))
    setMapHeight(next)
  }, [isDraggingMap])

  const handleMapDragEnd = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingMap) return
    ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)
    setIsDraggingMap(false)
    // The map stays exactly where it was let go. This used to snap to the
    // nearest of [22, 48, 80] for a "settled" feel, but a drag is an explicit
    // instruction about size — three stops meant almost every drag ended
    // somewhere the user hadn't chosen, and dragging to a deliberate height
    // was impossible.
    //
    // userMaxVhRef is the height the scroll-driven collapse eases DOWN from,
    // i.e. the height at scrollTop 0. If the page is already scrolled, the
    // released height is partway down that curve, so storing it raw would let
    // the next scroll event yank the map to a different size. Invert the ease
    // to find the ceiling that puts the map where they left it right now.
    const progress = Math.min(Math.max(window.scrollY / SCROLL_RANGE, 0), 1)
    const eased = 1 - Math.pow(1 - progress, 4)
    const remaining = 1 - eased
    userMaxVhRef.current =
      remaining > 0.01 ? Math.min(MAX_MAP_VH, mapHeight / remaining) : MAX_MAP_VH
  }, [isDraggingMap, mapHeight])

  if (loading) {
    return <PageLoader message={t("loading")} />
  }

  return (
    // Natural body scroll instead of h-dvh + inner-scroll-container.
    // This lets users free-scroll anywhere (heading, map, gap below
    // the drag handle) without having to grab the listings panel
    // first. Map is sticky-positioned below so it stays visible while
    // shrinking via the window scroll listener.
    // Chrome via <PageFrame chrome={userId ? "app" : "public"}>.
    // Public users get the centered 1svw frame; signed-in users get
    // the pill-clearance offset. Vertical pt is conditional too: authed
    // users clear the tall floating-pill chrome (~9rem); logged-out users
    // used to only need to clear PublicNavbar (~5rem, in-flow, so pt-2 was
    // nearly free). PublicNavbar was replaced by PublicNavGlass here — a
    // `fixed` floating pill that reserves no layout space at all — so pt-2
    // left the heading rendering right under/behind it. pt-24 clears the
    // floating pill the same way the shop detail page and other
    // PublicNavGlass pages already do.
    <PageFrame chrome={userId ? "app" : "public"}>
    <div className={`min-h-dvh bg-background pb-28 ${
      userId ? "pt-12 md:pt-20" : "pt-24 md:pt-6 lg:pt-8"
    }`}>
      {/* Fullscreen map overlay — mobile + desktop */}
      {mobileMapExpanded && (
        <div className="fixed inset-0 z-50 bg-black" data-floating-nav="hide">
          <ListingsMapDynamic listings={filteredListings} onListingSelect={handleMapListingSelect} onListingView={handleMapListingView} onListingClose={() => setSelectedMapListing(null)} focusCity={selectedCity === "all" ? null : selectedCity} isFullscreen={true} clientLens={clientLens} />
          {/* Top-right on every breakpoint. Bottom-centre fought the BOOTH
              launcher (same z-50 anchor) and covered the map preview card;
              bottom-right then landed on the map's own "locate me" button.
              Top-right is the one corner the map doesn't use. */}
          <button
            onClick={() => setMobileMapExpanded(false)}
            aria-label={t("map.showListings")}
            className="fixed top-4 right-4 z-[60] inline-flex items-center justify-center gap-2 w-12 h-12 md:w-auto md:h-auto md:px-5 md:py-2.5 bg-[#141414]/90 backdrop-blur-md shadow-lg rounded-full text-sm font-medium border border-white/10 text-white hover:bg-[#141414] transition-colors"
          >
            <svg className="w-5 h-5 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
            <span className="hidden md:inline">{t("map.showListings")}</span>
          </button>
        </div>
      )}

      {/* Page-header strip — safe-area-aware top padding so the
          heading sits right under the BOOTH wordmark instead of
          floating mid-screen. Heading bumped down to text-xl on
          desktop (was text-3xl) per design — ~33% smaller, reads
          as a section label rather than a hero title. */}
      {/* One gutter for the whole page — px-2.5 on mobile, px-4 from md up.
          The phone gutter went 16px -> 10px so the map and the cards get the
          width back; anything wider than a phone has room to spare and keeps
          the roomier inset. This heading was on lg:px-0
          while the listings grid below sat on lg:px-6, so "Browse Shops"
          started flush against the viewport and "Listings" was 24px in; the
          two headings never lined up.

          pb-4 rather than pb-7: the gap to the map is halved from where it
          was, which lands it between "crowding" and "adrift". Heading steps
          up ~15% at lg (text-xl -> text-[1.4rem]) so it carries the page
          instead of reading as a caption above a full-bleed map. */}
      {/* Logged-out phone: -mt-3 on top of the frame's pt-24 puts the heading
          at the same 84px the shop detail page's "View Shop" sits at, so the
          two pages line up when you go between them. */}
      <div className={`${userId ? "pt-4" : "-mt-3 pt-0"} md:mt-0 md:pt-5 lg:pt-0 pb-4 px-2.5 md:px-4`}>
        <h1 className="text-2xl md:text-3xl lg:text-[1.4rem] font-bold tracking-tight text-left text-foreground">
          {t("header.title")}
        </h1>
      </div>

      {/* Map Section — sticky-positioned on mobile/tablet so it stays
          at the top of the viewport while the user scrolls. Disabled
          on lg+ (lg:static) because the desktop card uses
          overflow-hidden for its rounded corners, which can't host a
          sticky descendant. On lg+ the map just scrolls off naturally
          — fine since the card pattern keeps the chrome (pill, bell,
          wordmark) visible regardless. */}
      <div
        className="static w-full px-2.5 md:px-3.5 lg:px-0"
        style={{
          height: `${mapHeight}svh`,
          // No CSS transition during scroll/drag — rAF-driven setMapHeight
          // already runs at frame rate, so a layered transition causes the
          // map to re-render against a gray bg between intermediate frames.
          willChange: 'height',
        }}
      >
        <div className="relative w-full h-full rounded-[2.5rem] overflow-hidden bg-[#141414]">
          <ListingsMapDynamic listings={filteredListings} onListingSelect={handleMapListingSelect} onListingView={handleMapListingView} onListingClose={() => setSelectedMapListing(null)} focusCity={selectedCity === "all" ? null : selectedCity} isFullscreen={false} clientLens={clientLens} />
          {/* Map controls — icon-only on mobile, icon + label on desktop */}
          {mapHeight > 10 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20">
              <button
                onClick={() => setMobileMapExpanded(true)}
                aria-label={t("map.expandMap")}
                title={t("map.expandMap")}
                className="inline-flex items-center justify-center gap-1.5 h-7 w-7 md:w-auto md:px-3 md:py-1.5 bg-[#141414]/90 backdrop-blur-md shadow-md rounded-full border border-white/10 hover:bg-booth-green/90 hover:border-booth-green transition-colors text-xs font-medium text-white"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
                <span className="hidden md:inline">{t("map.expandMap")}</span>
              </button>
              <button
                onClick={() => setListingsExpanded((v) => !v)}
                aria-label={listingsExpanded ? t("map.showMap") : t("map.expandListings")}
                title={listingsExpanded ? t("map.showMap") : t("map.expandListings")}
                className="inline-flex items-center justify-center gap-1.5 h-7 w-7 md:w-auto md:px-3 md:py-1.5 bg-[#141414]/90 backdrop-blur-md shadow-md rounded-full border border-white/10 hover:bg-booth-green/90 hover:border-booth-green transition-colors text-xs font-medium text-white"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {listingsExpanded ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  )}
                </svg>
                <span className="hidden md:inline">{listingsExpanded ? t("map.showMap") : t("map.expandListings")}</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Drag handle — splitter between map + listings. Pull up/down to resize. */}
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label={t("map.resizeHandle")}
        onPointerDown={handleMapDragStart}
        onPointerMove={handleMapDragMove}
        onPointerUp={handleMapDragEnd}
        onPointerCancel={handleMapDragEnd}
        className="relative h-3 -mt-1.5 z-20 flex items-center justify-center cursor-ns-resize touch-none select-none"
      >
        <div
          className={`rounded-full transition-all duration-200 ${
            isDraggingMap
              ? "w-14 h-1 bg-booth-green shadow-[0_0_0_4px_rgba(16,185,129,0.15)]"
              : "w-10 h-1 bg-muted-foreground/40 hover:w-12 hover:h-1.5 hover:bg-muted-foreground"
          }`}
        />
      </div>

      {/* Below-the-map content — filters + listings. Card-style
          rounded white surface (matches the map's iPhone-radius
          treatment) so it reads as a stacked pair with the map. */}
      <div className="mt-2 mx-2 lg:mx-2 mb-8">
        {/* Filter Bar — was sticky top-0 before the body-scroll
            refactor; now in normal flow so it doesn't fight the
            sticky map for the top-0 slot. Mirrors /hiring's filter
            placement (in flow, not sticky). */}
        {/* Hairline under the filters from md only. On a phone the strip and
            the Listings row below are one block of controls; a rule between
            them split it in two. */}
        <div className="md:border-b md:border-border">
          {/* Desktop filter row — one row, TWO independent horizontal
              scroll containers. Cities scrolls on the left (takes the
              remaining width), types scrolls on the right (capped so
              it doesn't dominate). A vertical divider sits between
              them; 'X results' is pinned to the far right. */}
          {/* Desktop: ONE row — search, type, country, city.
              Ordered the way the question gets asked: name it if you know it,
              otherwise narrow by what kind, then by where. Country sits before
              city because the funnel runs broad to narrow — picking a flag
              shortens the city list behind the trigger.

              There is deliberately no "All Types" pill. Its only job was to
              undo a choice nobody had made yet, so on first load it was the
              widest control in the row and did nothing. None-selected means
              all, which is also exactly how the flags beside it behave — one
              interaction model for the whole bar instead of two. */}
          <div className="hidden md:flex items-center gap-2 px-4 pt-3 pb-3">
            {/* Search */}
            <div className="relative flex-shrink-0 w-[min(18rem,26%)]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" strokeWidth={2} />
              <Input
                type="text"
                placeholder={t("search.placeholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-8 pl-9 pr-3 rounded-full bg-secondary border-0 text-foreground placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-ring text-[11px]"
              />
            </div>

            <span className="w-px h-4 bg-border flex-shrink-0" />

            {/* Types — toggles, no reset pill. */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {shopTypes.filter((ty) => ty.value !== "all").map((type) => (
                <button
                  key={type.value}
                  onClick={() => setSelectedType(selectedType === type.value ? "all" : type.value)}
                  aria-pressed={selectedType === type.value}
                  className={`px-2.5 py-1 text-[11px] font-medium rounded-full whitespace-nowrap transition-colors ${
                    selectedType === type.value
                      ? "bg-foreground text-background"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent"
                  }`}
                >
                  {t(`filters.types.${type.value}`)}
                </button>
              ))}
            </div>

            <span className="w-px h-4 bg-border flex-shrink-0" />

            {/* Countries — scrolls. The roster runs past what BOOTH has shops
                in on purpose; see lib/country-from-state. */}
            <div ref={countriesScrollRef} className="flex items-center gap-1 overflow-x-auto scrollbar-hide flex-shrink min-w-0 max-w-[24%]">
              {dynamicCountries.map((c) => (
                <button
                  key={c.code}
                  onClick={() => {
                    if (!c.count) return
                    setSelectedCountry(selectedCountry === c.code ? "all" : c.code)
                    setSelectedCity("all")
                  }}
                  disabled={!c.count}
                  title={c.count ? `${c.name} — ${c.count} ${c.count === 1 ? "shop" : "shops"}` : `${c.name} — coming soon`}
                  aria-label={c.name}
                  className={`flex items-center justify-center px-1.5 py-1 rounded-full flex-shrink-0 transition-all ${
                    selectedCountry === c.code
                      ? "bg-foreground/10 ring-1 ring-inset ring-foreground/25"
                      : c.count
                        ? "opacity-60 hover:opacity-100"
                        // No shops yet — dimmed but in full colour. A row of
                        // grey flags reads as broken; the point of showing a
                        // country BOOTH has not reached is that you recognise
                        // it, and half of recognising a flag is its colour.
                        : "opacity-55 cursor-default"
                  }`}
                >
                  <CountryFlag code={c.code} className="w-[18px] h-auto" />
                </button>
              ))}
            </div>

            <span className="w-px h-4 bg-border flex-shrink-0" />

            {/* Every city, for when scrolling the row is the wrong tool. */}
            <div className="relative flex-shrink-0">
              <button
                onClick={() => setFilterModalOpen((v) => !v)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-full whitespace-nowrap transition-colors ${
                  selectedCity !== "all"
                    ? "bg-foreground text-background"
                    : "bg-accent text-foreground hover:bg-accent/70"
                }`}
              >
                {selectedCity !== "all" ? selectedCity : "City"}
                <ChevronDown className="w-3 h-3" strokeWidth={2} />
              </button>
              {/* Hangs from the RIGHT. This trigger sits at the right end of
                  a wide bar, and left-aligned the panel ran ~140px off the
                  edge of the window in production — the measured nudge inside
                  the component is a fallback, not something to rely on. */}
              <ListingsFilterModal
                open={filterModalOpen}
                onOpenChange={setFilterModalOpen}
                cities={dynamicCities}
                selectedCity={selectedCity}
                onCityChange={setSelectedCity}
                align="right"
                activeCount={activeFilterCount}
                onClear={clearAllFilters}
              />
            </div>

            {/* Cities, inline. The flags used to hold flex-1 and pad the row
                out with dead space to the left of the City trigger; cities
                fill it instead and scroll when they run long. The trigger
                stays at the end as the way to reach ALL of them — inline is
                for browsing what is nearby, the popover is for finding a
                specific one. */}
            {/* min-w is load-bearing. Search, types and flags are all
                flex-shrink-0, so on a 1100-1400px window this row was the only
                thing left to squeeze and it collapsed to ZERO width — 43 city
                pills rendered into a 0px box. The row scrolls internally, so a
                floor costs nothing and guarantees the cities are reachable
                without opening the popover. */}
            <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide flex-1 min-w-[180px]">
              {dynamicCities.filter((c) => c.value !== "all").map((city) => (
                <button
                  key={city.value}
                  onClick={() => setSelectedCity(selectedCity === city.value ? "all" : city.value)}
                  aria-pressed={selectedCity === city.value}
                  className={`px-2.5 py-1 text-[11px] font-medium rounded-full whitespace-nowrap flex-shrink-0 transition-colors ${
                    selectedCity === city.value
                      ? "bg-foreground text-background"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent"
                  }`}
                >
                  {city.label}
                </button>
              ))}
            </div>

            <span className="w-px h-4 bg-border flex-shrink-0" />

            <span className="text-[11px] text-muted-foreground flex-shrink-0 tabular-nums min-w-[2.5rem] text-right">
              {filteredListings.length}
            </span>
          </div>

          {/* Mobile Row 1: Search bar */}
          <div className="md:hidden px-2.5 pt-3 pb-2">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <Input
                type="text"
                placeholder={t("search.placeholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-9 pl-9 pr-3 rounded-full bg-secondary border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring text-xs"
              />
            </div>
          </div>

          {/* Mobile Row 2: one swipeable strip, Filters pinned right.
              In international mode the flags lead, then a divider, then the
              shop types; in US mode it is just the types. They used to be
              either/or, so going international hid Barbershop / Studio /
              Suite / Salon entirely. One strip keeps both a swipe apart,
              and the fade on the right edge says there is more to swipe.
              No "Countries" or "All types" reset pill: tapping the selected
              flag or type clears it, the same gesture everywhere in the bar. */}
          <div className="md:hidden px-2.5 pb-3 flex items-center gap-2">
            <div
              className="flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto scrollbar-hide pr-6"
              style={{
                maskImage: "linear-gradient(90deg, #000 calc(100% - 1.5rem), transparent)",
                WebkitMaskImage: "linear-gradient(90deg, #000 calc(100% - 1.5rem), transparent)",
              }}
            >
              {intlMode && (
                <>
                  {dynamicCountries.map((c) => (
                    <button
                      key={c.code}
                      onClick={() => {
                        if (!c.count) return
                        setSelectedCountry(selectedCountry === c.code ? "all" : c.code)
                        setSelectedCity("all")
                      }}
                      disabled={!c.count}
                      aria-label={c.name}
                      aria-pressed={selectedCountry === c.code}
                      // px-1 rather than px-1.5: six flags at the tighter
                      // padding leave room for the first types to show
                      // before anyone swipes.
                      className={`flex h-7 items-center justify-center px-1 rounded-full flex-shrink-0 transition-all ${
                        selectedCountry === c.code
                          ? "bg-foreground/10 ring-1 ring-inset ring-foreground/25"
                          : c.count
                            ? "opacity-70"
                            : "opacity-50"
                      }`}
                    >
                      <CountryFlag code={c.code} className="w-[18px] h-auto" />
                    </button>
                  ))}
                  <span aria-hidden className="mx-1 h-4 w-px flex-shrink-0 bg-border" />
                </>
              )}
              {shopTypes
                .filter((ty) => ty.value !== "all")
                .map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setSelectedType(selectedType === type.value ? "all" : type.value)}
                    aria-pressed={selectedType === type.value}
                    className={`h-7 px-2.5 text-[11px] font-medium rounded-full whitespace-nowrap flex-shrink-0 transition-colors ${
                      selectedType === type.value
                        ? "bg-foreground text-background"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent"
                    }`}
                  >
                    {t(`filters.types.${type.value}`)}
                  </button>
                ))}
            </div>

            <span className="hidden sm:block w-px h-4 bg-border flex-shrink-0" />

            {/* Cities, inline and scrolling — same split as desktop: this row
                is for browsing what is nearby, the Filters panel is for
                finding a specific one in the whole list. Tapping the selected
                one clears it, so no "All Cities" pill.
                Not on a phone: after the four types there was room for three
                letters of one city ("Arlingtor"). City lives in Filters there;
                the inline list comes back from sm, where it has the width. */}
            <div className="hidden sm:flex items-center gap-1.5 overflow-x-auto scrollbar-hide flex-1 min-w-0">
              {dynamicCities
                .filter((c) => c.value !== "all")
                .map((city) => (
                  <button
                    key={city.value}
                    onClick={() => setSelectedCity(selectedCity === city.value ? "all" : city.value)}
                    aria-pressed={selectedCity === city.value}
                    className={`px-2.5 py-1 text-[11px] font-medium rounded-full whitespace-nowrap flex-shrink-0 transition-colors ${
                      selectedCity === city.value
                        ? "bg-foreground text-background"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent"
                    }`}
                  >
                    {city.label}
                  </button>
                ))}
            </div>

            <div className="relative flex-shrink-0 ml-auto sm:ml-0">
              <button
                onClick={() => setFilterModalOpen((v) => !v)}
                className={`inline-flex h-7 items-center gap-1 px-2.5 text-[11px] font-medium rounded-full whitespace-nowrap transition-colors ${
                  selectedType !== "all" || selectedCity !== "all"
                    ? "bg-foreground text-background"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                Filters
                {(selectedType !== "all" ? 1 : 0) + (selectedCity !== "all" ? 1 : 0) > 0 && (
                  <span className="tabular-nums">
                    {(selectedType !== "all" ? 1 : 0) + (selectedCity !== "all" ? 1 : 0)}
                  </span>
                )}
                <ChevronDown className="w-3 h-3" strokeWidth={2} />
              </button>
              <ListingsFilterModal
                open={filterModalOpen}
                onOpenChange={setFilterModalOpen}
                types={shopTypes.map((ty) => ({ value: ty.value, label: t(`filters.types.${ty.value}`) }))}
                selectedType={selectedType}
                onTypeChange={setSelectedType}
                cities={dynamicCities}
                selectedCity={selectedCity}
                onCityChange={setSelectedCity}
                activeCount={activeFilterCount}
                onClear={clearAllFilters}
                // Mobile only: desktop shows flags and cities side by side and
                // has no mode to switch.
                intlMode={intlMode}
                onIntlModeChange={changeIntlMode}
                // Last control in the mobile row — opening rightward from here
                // put most of the panel past the screen edge.
                align="right"
              />
            </div>
          </div>
        </div>

        {/* Listings Grid */}
        {/* Same gutter as the heading and filter row above. lg:px-6 gave
            the grid 8px more inset than everything else on the page and cost
            the cards width they had better uses for. */}
        <div ref={listingsContainerRef} className="px-2.5 md:px-4 pt-1 md:pt-4 pb-24">
          {/* One control line: label left, sort + density right. These were
              two stacked rows — sort alone on the first, heading and density
              on the second — which spent a whole row of height on a single
              right-aligned pill and left the heading orphaned from the
              controls it belongs with. mb-6 gives the cards room; at mb-3 the
              first row of photos crowded the label. */}
          {/* scroll-mt so the sticky chrome does not sit over the heading we
              just scrolled to. */}
          <div ref={listingsTopRef} className="flex items-center justify-between gap-3 mb-4 md:mb-6 scroll-mt-20">
            {/* Count as a small badge beside the title, the same as the job
                board's header, so the two browse pages read alike. */}
            <h2 className="flex items-center gap-1.5 text-base md:text-lg font-semibold tracking-tight text-foreground">
              {t("section.title")}
              <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-muted px-1.5 text-[11px] font-medium tabular-nums text-muted-foreground">
                {filteredListings.length}
              </span>
            </h2>
            <div className="flex items-center gap-2">
            <div className="relative inline-block">
              <SortTrigger value={sortMode} open={sortOpen} onClick={() => setSortOpen(v => !v)} />
              <ListingsSortModal
                open={sortOpen}
                onOpenChange={setSortOpen}
                value={sortMode}
                onChange={setSortMode}
              />
            </div>
            <div className="flex items-center gap-1 p-0.5 rounded-lg bg-muted">
              <button
                onClick={() => setViewDensity("sparse")}
                className={`p-1.5 rounded-md transition-colors ${viewDensity === "sparse" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                aria-label={t("section.sparseAria")}
              >
                <Rows className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setViewDensity("dense")}
                className={`p-1.5 rounded-md transition-colors ${viewDensity === "dense" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                aria-label={t("section.denseAria")}
              >
                <LayoutGrid className="w-3.5 h-3.5" />
              </button>
            </div>
            </div>
          </div>

          {pinnedListing && (
            <div className="mb-3 flex items-center gap-2">
              {/* Built from the primary button rather than from a muted
                  badge: the same black edge, lightened into glass. A solid
                  grey fill made it read as a disabled control, when it is
                  the one thing on the page the viewer just did.

                  The gloss is a top-edge inset highlight over a vertical
                  white wash, so the pill catches light along its upper rim
                  the way the black buttons do, without taking their weight.
                  border-foreground rather than a literal black so the edge
                  inverts to white in dark mode instead of vanishing into
                  the page. */}
              <span
                className={cn(
                  "inline-flex max-w-full items-center gap-1.5 rounded-full py-1 pl-2.5 pr-1",
                  "text-[11px] font-medium",
                  "border border-foreground/85 backdrop-blur-sm",
                  "bg-gradient-to-b from-white/75 to-white/35",
                  "dark:from-white/15 dark:to-white/5",
                  "shadow-[inset_0_1px_0_rgba(255,255,255,0.75)] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.14)]",
                )}
              >
                <MapPin className="h-3 w-3 shrink-0 text-foreground" />
                <span className="truncate text-foreground/55">
                  <span className="font-semibold text-foreground">{pinnedListing.name}</span> from the map
                </span>
                <button
                  type="button"
                  onClick={() => setPinnedFromMap(null)}
                  className="ml-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-foreground/55 transition-colors hover:bg-foreground/10 hover:text-foreground"
                  aria-label="Stop showing this shop first"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            </div>
          )}

          {loading && listings.length === 0 ? (
            <div className={viewDensity === "dense"
              ? "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3"
              : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"}>
              <ListingCardSkeleton />
              <ListingCardSkeleton />
              <ListingCardSkeleton />
              <ListingCardSkeleton />
              <ListingCardSkeleton />
            </div>
          ) : (
            <div className={viewDensity === "dense"
              ? "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3"
              : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"}>
              {displayListings.map((listing) => (
                <div
                  key={listing.id}
                  id={`listing-${listing.id}`}
                  className={cn(
                    // Same radius as ListingCard's root (rounded-3xl, above), so
                    // the ring's corners follow the card's instead of cutting
                    // inside them.
                    "rounded-3xl transition-shadow",
                    listing.id === pinnedFromMap && "ring-2 ring-foreground ring-offset-2 ring-offset-background",
                  )}
                >
                  <ListingCard
                    listing={listing}
                    savedListings={savedListings}
                    savingId={savingId}
                    onToggleSave={handleToggleSave}
                    onLearnMore={handleLearnMore}
                    isOwner={role === "owner"}
                    clientLens={clientLens}
                  />
                </div>
              ))}
            </div>
          )}

          {filteredListings.length === 0 && !loading && (
            <div className="text-center py-12">
              <svg
                className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
              <h3 className="text-lg font-semibold text-foreground mb-2">{t("empty.title")}</h3>
              {/* Say WHY it is empty. "Be the first to list your shop" is only
                  true when nothing is filtered; with a filter on it told the
                  reader the platform was empty when the shop they wanted was
                  one click away. */}
              {selectedCity !== "all" || selectedType !== "all" || selectedCountry !== "all" || searchQuery.trim() ? (
                <p className="text-muted-foreground mb-4">{t("empty.filtered")}</p>
              ) : (
                <p className="text-muted-foreground mb-4">{t("empty.subtitle")}</p>
              )}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  // Was variant="outline" bg-transparent: dark text on a dark
                  // page behind a hairline border, which on this background
                  // read as nothing at all. It is the one control that can
                  // rescue someone from an empty result, so it gets a fill.
                  className="bg-accent text-foreground hover:bg-accent/70"
                  onClick={() => {
                    setSelectedCity("all")
                    setSelectedType("all")
                    // Country was missing here, so "Clear all filters" left the
                    // filter most likely to be causing the empty result switched
                    // on — the button could not do the one job it has.
                    setSelectedCountry("all")
                    setSearchQuery("")
                  }}
                >
                  {t("empty.clearFilters")}
                </Button>
                <Button className="bg-foreground text-background hover:bg-gray-800" onClick={() => router.push('/dashboard/owner/create-shop')}>
                  {t("empty.listYourShop")}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sign Up Modal for unauthenticated users trying to save */}
      <SignUpPromptModal
        isOpen={showSignUpModal}
        onClose={() => setShowSignUpModal(false)}
        title={t("signupPrompt.title")}
        description={t("signupPrompt.description")}
      />
    </div>
    </PageFrame>
  )
}
